<template>
  <div tabindex="-1" class="customscroll ma-0 pa-2" style="width: 100%; height: 100%; max-height: 100%" align="center">
    <v-row class="ma-0 pa-2">
      <v-col class="ma-0 pa-2">
        <v-alert v-if="plans.map((d) => d.daily).sum() == 0" type="info" class="ma-1 mb-8 text-left" dense width="100%">
          <span>{{ TR.PleaseChooseSubscriptionPlan }}</span>
        </v-alert>
        <v-row v-else class="ma-0 pa-0 pb-2" justify="start" align="center">
          <v-radio-group hide-details class="ma-2 pa-0" v-model="bySum">
            <v-radio :value="true" :label="TR.IfIPay"></v-radio>
            <v-radio :value="false" :label="TR.ToWorkUntil"></v-radio>
          </v-radio-group>
          <v-row align="center" class="ma-2 pa-2">
            <v-text-field
              style="max-width: 100px"
              v-if="bySum"
              outlined
              :disabled="plans.map((d) => d.daily).sum() == 0"
              class="centered-input"
              v-model.number="balanceNeed"
              @input="recalc"
              @keypress="isNumber3digits($event, balanceNeed)"
              single-line
              dense
              hide-details
            ></v-text-field>
            <v-dialog v-else ref="refDialogDate" v-model="modalEndDate" :return-value.sync="endDate" width="290px">
              <template v-slot:activator="{ on }">
                <v-text-field
                  style="max-width: 150px"
                  outlined
                  :disabled="plans.map((d) => d.daily).sum() == 0"
                  class="centered-input"
                  v-on="on"
                  maxlength="10"
                  :value="plans.map((d) => d.daily).sum() == 0 ? '∞' : new Date(endDate).toTremolFpStringShort()"
                  dense
                  hide-details
                ></v-text-field>
              </template>
              <v-date-picker firstDayOfWeek="1" type="date" :min="getMinDate()" :locale="TR.Locale" v-model="endDate">
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="modalEndDate = false">{{ TR.Cancel }}</v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="
                    $refs.refDialogDate.save(endDate);
                    recalc();
                  "
                  >{{ TR.OK }}</v-btn
                >
              </v-date-picker>
            </v-dialog>
            <template>
              <span v-if="bySum" class="ma-2 pa-2 subtitle-1">{{ TR.MoneyAbbreviation + " " + TR.TheBalanceWillBeUntil + " " + new Date(endDate).toTremolFpStringShort() }}</span>
              <span v-else class="ma-2 pa-2 subtitle-1">{{ TR.IsNecessaryToPay + " " + (0 > balanceNeed ? "0" : balanceNeed.toFixed(2)) + " " + TR.MoneyAbbreviation + " / " + (0 > balanceNeed ? "0" : toEuro(balanceNeed).toFixed(2)) + " " + TR.MoneyAbbreviation2 }}</span>
            </template>
          </v-row>
        </v-row>
        <v-row justify="center" class="ma-0 pa-0">
          <v-simple-table style="width: 100%">
            <thead>
              <tr>
                <th class="text-center">{{ TR.SubscriptionPlan }}</th>
                <th class="text-center">{{ TR.DevPriceForDay }}</th>
                <th class="text-center">{{ TR.DevsCount }}</th>
                <th class="text-center">{{ TR.ConsumptionForDay }}</th>
                <th class="text-center">{{ TR.ConsumptionFor30Days }}</th>
                <th class="text-center">{{ TR.ConsumptionFor365Days }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="plan in plans" :key="plan.id" @mouseover="currentPlan = plan">
                <td class="text-center">
                  <b :style="`color:${Colors.IconColor}`">{{ `${plan.name}` }}</b>
                  <v-menu v-if="!isOnMobile && currentPlan" open-on-hover bottom offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-on="on" v-bind="attrs" icon small color="info">
                        <v-icon small>mdi-information</v-icon>
                      </v-btn>
                    </template>
                    <t-subplan :subPlan="plan"></t-subplan>
                  </v-menu>
                </td>
                <td class="text-center" style="white-space: pre">
                  {{
                    `${plan.dailyForOne.toFixed(4)} ${TR.MoneyAbbreviation}\n
                      ${toEuro(plan.dailyForOne).toFixed(4)} ${TR.MoneyAbbreviation2}`
                  }}
                </td>
                <td align="center">
                  <v-text-field class="centered-input ma-2 mb-0 pa-0" dense v-model.number="plan.devsCount" @input="calcPlan(plan)" :max="remainingDevs(plan)" hide-details single-line @keypress="isNumber3digits($event, plan.devsCount)"></v-text-field>
                  <t-divider class="ma-0 pa-0 mx-6" style="max-width: 50px"></t-divider>
                  <v-slider class="ma-0 pa-0" style="width: 100%" v-model.number="plan.devsCount" @change.self="calcPlan(plan)" :max="remainingDevs(plan)" hide-details :min="0"></v-slider>
                </td>
                <td class="text-right" style="width: 120px; white-space: pre">{{ `${plan.daily.toFixed(4)} ${TR.MoneyAbbreviation}\n ${toEuro(plan.daily).toFixed(4)} ${TR.MoneyAbbreviation2}` }}</td>
                <td class="text-right" style="width: 120px; white-space: pre">{{ `${plan.monthly.toFixed(2)} ${TR.MoneyAbbreviation}\n ${toEuro(plan.monthly).toFixed(2)} ${TR.MoneyAbbreviation2}` }}</td>
                <td class="text-right" style="width: 120px; white-space: pre">{{ `${plan.yearly.toFixed(2)} ${TR.MoneyAbbreviation}\n ${toEuro(plan.yearly).toFixed(2)} ${TR.MoneyAbbreviation2}` }}</td>
              </tr>
              <tr>
                <td class="text-center">{{ TR.Total + ":" }}</td>
                <td class="text-right"></td>
                <td class="text-center">
                  {{
                    plans
                      .filter((d) => d.devsCount)
                      .map((d) => d.devsCount)
                      .sum()
                      .toFixed(0) + (discountPercent ? " " + TR.PCS + " (" + TR.Discount + " " + discountPercent.toFixed(2) + "%)" : "")
                  }}
                </td>
                <td class="text-right" style="white-space: pre">
                  <span style="white-space: pre">
                    {{
                      `${plans
                        .map((d) => d.daily)
                        .sum()
                        .toFixed(4)} ${TR.MoneyAbbreviation}\n${toEuro(plans.map((d) => d.daily).sum()).toFixed(4)} ${TR.MoneyAbbreviation2}`
                    }}
                  </span>
                </td>
                <td class="text-right" style="white-space: pre">
                  {{
                    `${plans
                      .map((d) => d.monthly)
                      .sum()
                      .toFixed(2)} ${TR.MoneyAbbreviation}\n${toEuro(plans.map((d) => d.monthly).sum()).toFixed(2)} ${TR.MoneyAbbreviation2}`
                  }}
                </td>
                <td class="text-right" style="white-space: pre">
                  {{
                    `${plans
                      .map((d) => d.yearly)
                      .sum()
                      .toFixed(2)} ${TR.MoneyAbbreviation}\n${toEuro(plans.map((d) => d.yearly).sum()).toFixed(2)} ${TR.MoneyAbbreviation2}`
                  }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-row>

        <v-row class="ma-0 pa-0" v-if="isDistributor" justify="center" style="width: 100%; overflow: hidden">
          <v-card class="ma-0 pa-2 pt-6" width="100%" :height="350">
            <DiscountChart ref="refDiscountChart"></DiscountChart>
          </v-card>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import DataManager, { ErrorMessage } from "../../js/DataManager";
import { TR } from "../../main";
import DiscountChart from "./DiscountChart";

export default {
  components: {
    DiscountChart,
  },
  data() {
    return {
      distributorMaxDevices: 600,
      discounts: null,
      bySum: false,
      modalEndDate: false,
      endDate: new Date().addYears(1).toStringWithFormat("yyyy-MM-dd"),
      balance: 0,
      balanceNeed: 0,
      devs: [],
      datatype: null,
      plans: [],
      currentPlan: null,
      TR: TR,
    };
  },
  computed: {
    isDistributor() {
      return DataManager.getData(DataManager.DataType.user).isDistributor;
    },
    discountPercent() {
      const dc = this.plans
        .filter((d) => d.devsCount)
        .map((d) => d.devsCount)
        .sum()
        .toFixed(0);
      var discPerc = 0;
      if (dc > 0) {
        var fdisc = this.discounts.find((d) => d.fuCount == dc);
        if (fdisc) {
          discPerc = fdisc.percent;
        } else {
          discPerc = 50;
        }
      }
      return discPerc;
    },
  },
  methods: {
    setTitleAndData(meta, error) {
      this.datatype = meta.datatype;
      this.title = meta.title;
      if (error.ErrorCode == ErrorMessage.ERROR_CODE_OK) {
        this.devs = DataManager.getData(DataManager.DataType.devices, true).filter((d) => d.duprofile && d.duprofile.cosmosableDevice);
        this.balance = DataManager.getData(DataManager.DataType.user, true).balance;
        this.plans = DataManager.getData(this.datatype)
          .filter((p) => p.id > 0)
          .map((plan) => ({
            id: plan.id,
            name: plan.name,
            price: plan.price,
            pricePerDay: plan.pricePerDay,
            description: plan.description,
            dailyForOne: 0,
            daily: 0,
            monthly: 0,
            yearly: 0,
            devsCount: 0,
          }));
        this.$nextTick(() => {
          if (this.devs && this.devs.length > 0) {
            this.plans.forEach((p) => {
              p.devsCount = this.devs.filter((d) => d.duprofile != null && d.duprofile.csubscriptionPlan != null && d.duprofile.csubscriptionPlan.id == p.id).length;
              this.calcPlan(p);
            });
            this.$nextTick(() => {
              this.currentPlan = this.plans[0];
              this.recalc();
              // const dailyConsumation = this.plans.map((d) => d.daily).sum();
              // if (dailyConsumation != 0) {
              //   this.endDate = new Date().addDays((this.balance / dailyConsumation).roundTo(0)).toStringWithFormat("yyyy-MM-dd");
              // }
            });
          }
        });
      } else {
        this.message = error.Message;
      }
    },
    setFocus(on) {
      if (on && this.$refs.refDiscountChart && this.$refs.refDiscountChart.refreshTheme) {
        this.$refs.refDiscountChart.refreshTheme();
      }
    },
    remainingDevs(plan) {
      if (this.devs.length <= 0) return this.distributorMaxDevices;
      return (
        this.devs.length -
        this.plans
          .filter((p) => p.devsCount)
          .map((p) => p.devsCount)
          .sum() +
        plan.devsCount
      );
    },
    isNumber3digits(event, value) {
      if (!/^[0-9]{0,3}$/.test(event.key) || value instanceof Number || (value && value.toString().length > 3)) event.preventDefault();
    },
    calcPlan(plan) {
      if (plan.devsCount instanceof String) {
        plan.devsCount = 0;
      }
      const rd = this.remainingDevs(plan);
      this.$nextTick(() => {
        if (plan.devsCount > rd) {
          plan.devsCount = rd;
          this.plans.forEach((p) => {
            if (p.id != plan.id) p.devsCount = 0;
          });
        }
        this.plans.forEach((p) => this.recalcPlanDiscount(p));
      });
    },
    recalcPlanDiscount(plan) {
      this.$nextTick(() => {
        const dc = plan.devsCount;
        const ppd = ((plan.pricePerDay * (100 - this.discountPercent)) / 100).roundTo(4);
        plan.dailyForOne = (1 * ppd).roundTo(4);
        plan.daily = (dc * ppd).roundTo(4);
        plan.monthly = (dc * ppd * 30).roundTo(4);
        plan.yearly = (dc * ppd * 365).roundTo(4);
        this.$nextTick(() => {
          this.recalc();
        });
      });
    },
    recalc() {
      if (this.bySum) {
        this.balanceNeedChanged();
      } else {
        this.endDateChanged();
      }

      //TODO
    },
    getMinDate() {
      return new Date()
        .addDays(
          this.balance /
            this.plans
              .map((d) => d.daily)
              .sum()
              .roundTo(2)
        )
        .toStringWithFormat("yyyy-MM-dd");
    },
    endDateChanged() {
      const diffTime = Math.abs(this.endDate.parseDateWithFormat("yyyy-MM-dd") - new Date());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const needed = (
        this.plans
          .map((d) => d.daily)
          .sum()
          .roundTo(4) *
          diffDays -
        this.balance
      ).roundTo(2);
      if (this.balanceNeed != needed) {
        this.balanceNeed = needed;
      }
      if (this.balanceNeed < 0) {
        this.balanceNeed = 0;
        this.balanceNeedChanged();
      }
    },
    balanceNeedChanged() {
      const days = ((this.balance + this.balanceNeed) / this.plans.map((d) => d.daily).sum()).roundTo(0);
      var nd = new Date().addDays(days);
      if (nd.toStringWithFormat("yyyy-MM-dd") != this.endDate && days != Infinity) {
        this.endDate = nd.toStringWithFormat("yyyy-MM-dd");
      }
    },
  },
  // watch: {
  //   endDate: function () {
  //     this.endDateChanged();
  //   },
  // },
  mounted() {
    this.$nextTick(() => {
      this.bySum = true;
    });
    this.discounts = DataManager.getData(DataManager.DataType.discounts);
  },
};
</script>
<style scoped>
.centered-input >>> * .v-input__slot:before,
.centered-input >>> * .v-input__slot:after {
  border-style: none;
}

.centered-input /deep/ input {
  padding: 0;
  margin: 0;
}

* >>> .v-slider--horizontal {
  min-height: 15px;
}
</style>